import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, TrendingUp, AlertTriangle, CheckCircle, Target, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const PipelineManagementAI = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Pipeline Management",
      "applicationCategory": "BusinessApplication",
      "description": "AI-powered pipeline management that automatically tracks deal health, predicts close rates, detects at-risk deals, and generates evidence-based revenue forecasts.",
      "url": "https://noq5.ai/pipeline-management-ai"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "Pipeline Management AI", "item": "https://noq5.ai/pipeline-management-ai" }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="AI Pipeline Management — Predict Revenue, Detect Risk | NoQ5"
        description="AI-powered pipeline management that auto-tracks deal health, predicts close rates with 35% more accuracy, and alerts you to at-risk deals within 48 hours. No more gut-feel forecasting."
        keywords="AI pipeline management, pipeline management AI, AI pipeline forecasting, sales pipeline AI, pipeline intelligence, deal health score, pipeline risk detection, AI revenue forecasting, predictive pipeline management, pipeline automation AI, sales pipeline automation, AI deal tracking"
        canonicalUrl="https://noq5.ai/pipeline-management-ai"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">Pipeline Management AI</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">AI Pipeline Management</span>:<br />Your Pipeline Finally Tells the Truth
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Stop relying on gut feel and rep optimism. NoQ5's AI pipeline management analyzes actual buyer engagement to predict close rates, detect at-risk deals, and generate forecasts backed by evidence — not opinions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial"><Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">See Your Real Pipeline <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              <Link to="/crm-agent"><Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Full CRM Agent</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-primary mb-1">35%</div><div className="text-sm text-muted-foreground">More Accurate Forecasts</div></div>
            <div><div className="text-3xl font-bold text-primary mb-1">48h</div><div className="text-sm text-muted-foreground">Risk Detection Speed</div></div>
            <div><div className="text-3xl font-bold text-primary mb-1">25%</div><div className="text-sm text-muted-foreground">Faster Pipeline Velocity</div></div>
            <div><div className="text-3xl font-bold text-primary mb-1">0</div><div className="text-sm text-muted-foreground">Manual Forecast Inputs</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Why Traditional Pipeline Management Fails</h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>Every sales leader has the same problem: your pipeline is a lie. Not because anyone is intentionally misleading — but because the data underneath it is incomplete, outdated, and based on rep self-reporting rather than actual buyer behavior.</p>
            
            <div className="grid md:grid-cols-2 gap-4 not-prose my-8">
              {[
                { problem: "Deals sit at the same stage for weeks with no real progress", impact: "Pipeline looks healthy but velocity is stalled" },
                { problem: "Close dates get pushed quarter after quarter", impact: "Forecast becomes fiction — board loses trust" },
                { problem: "Champions go dark but nobody notices for 2-3 weeks", impact: "Deals die silently while reps chase new logos" },
                { problem: "Reps submit 'Commit' based on gut feel, not evidence", impact: "Forecast accuracy stays at 40-50%" },
              ].map((item) => (
                <div key={item.problem} className="bg-destructive/5 border border-destructive/20 rounded-xl p-5">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.problem}</p>
                      <p className="text-xs text-muted-foreground mt-1">{item.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-foreground">How AI Pipeline Management Works</h3>
            <p>NoQ5's <Link to="/crm-agent" className="text-primary hover:underline">CRM AI agent</Link> doesn't just store pipeline data — it generates pipeline intelligence from actual buyer behavior:</p>
            
            <ol className="list-decimal pl-6 space-y-3">
              <li><strong>Engagement Analysis</strong> — Measures actual buyer engagement velocity: email response times, meeting attendance, multi-threading depth, and content engagement.</li>
              <li><strong>Signal Extraction</strong> — Identifies buying signals, objections, competitive mentions, and stakeholder sentiment from every conversation.</li>
              <li><strong>Health Scoring</strong> — Each deal receives a health score based on 20+ signals — not rep opinion. Scores update after every interaction.</li>
              <li><strong>Risk Detection</strong> — Proactive alerts within 48 hours: declining engagement, missed follow-ups, single-threaded deals, champion changes.</li>
              <li><strong>Evidence-Based Forecasting</strong> — Forecast categories assigned based on actual evidence: buyer actions, not rep confidence levels.</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Make Your Pipeline Trustworthy</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">Evidence-based forecasts. Proactive risk alerts. Zero manual inputs. Deploy in 15 minutes.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/ai-for-cros"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">AI for CROs</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/ai-for-cros" className="text-primary hover:underline text-sm">AI for CROs</Link>
            <Link to="/lead-qualification-ai" className="text-primary hover:underline text-sm">Lead Qualification AI</Link>
            <Link to="/why-crm-data-is-broken" className="text-primary hover:underline text-sm">Why CRM Data Is Broken</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/blog/crm-automation-guide" className="text-primary hover:underline text-sm">CRM Automation Guide</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PipelineManagementAI;
